import { Component, inject, input, output } from '@angular/core';
import { GameComponent, ILpSaveManager, MemorySaveManager, provideLeerplusApi } from '@gametailors/leerplus';
import { IStudioDataPackageProvider } from '@gametailors/lp-studio-connector';
import { RID_MECHANIC_DEFAULT_BACKGROUND } from '../components/rid-mechanic-background/rid-mechanic-background';
import { RidAlgoritmeActivity } from './activities/rid-algoritme-activity/rid-algoritme-activity';
import { RidLeeslijstActivity } from './activities/rid-leeslijst-activity/rid-leeslijst-activity';
import { RidScrapbookActivity } from './activities/rid-scrapbook-activity/rid-scrapbook-activity';
import { RidScrapbookViewerActivity } from './activities/rid-scrapbook-viewer-activity/rid-scrapbook-viewer-activity';
import { RidShowRewardActivity } from './activities/rid-show-reward-activity/rid-show-reward-activity';
import { RidWisselklankenActivity } from './activities/rid-wisselklanken-activity/rid-wisselklanken-activity';
import { RidAssignmentsCanvasComponent } from './canvas-components/rid-assignments-canvas-component/rid-assignments-canvas-component';
import { RidLocationLabelCanvasComponent } from './canvas-components/rid-location-label-canvas-component/rid-location-label-canvas-component';
import { RidLocationStopsCanvasComponent } from './canvas-components/rid-location-stops-canvas-component/rid-location-stops-canvas-component';
import { RidLocationTitleCanvasComponent } from './canvas-components/rid-location-title-canvas-component/rid-location-title-canvas-component';
import { RidProvinceCanvasComponent } from './canvas-components/rid-province-canvas-component/rid-province-canvas-component';
import { RidProvinceLabelCanvasComponent } from './canvas-components/rid-province-label-canvas-component/rid-province-label-canvas-component';
import { RidCalendarDebugTab } from './components/rid-calendar-debug-tab/rid-calendar-debug-tab';
import { RidHomeworkDebugTab } from './components/rid-homework-debug-tab/rid-homework-debug-tab';
import { getDefaultCodeSave, IRidCodeLeerplusSaveState, LEERPLUS } from './rid-game.utils';
import { RidCalendarService } from './services/rid-calendar.service';
import { RidHomeworkService } from './services/rid-homework.service';
import { RidJourneyService } from './services/rid-journey.service';
import { RidRewardService } from './services/rid-reward.service';
import { RidScrapbookService } from './services/rid-scrapbook.service';

@Component({
  selector: 'rid-game',
  imports: [GameComponent],
  providers: [
    ...provideLeerplusApi(),
    RidCalendarService,
    RidHomeworkService,
    RidJourneyService,
    RidRewardService,
    RidScrapbookService,
    { provide: RID_MECHANIC_DEFAULT_BACKGROUND, useValue: false },
  ],
  templateUrl: './rid-game.html',
  host: {
    class: 'block size-full',
  },
})
export class RidGame {
  private readonly $lpApiService = inject(LEERPLUS.apiToken);
  private readonly $ridHomeworkService = inject(RidHomeworkService);
  private readonly $ridRewardService = inject(RidRewardService);

  mission = input<string>();
  missionCompleted = output<string>();

  studioDataPackageProvider = input.required<IStudioDataPackageProvider>();

  saveManager = input<ILpSaveManager<IRidCodeLeerplusSaveState>>(new MemorySaveManager(getDefaultCodeSave()));
  extension = LEERPLUS.createExtension() //
    .defineCanvasComponent('RidLocationStopsCanvasComponent', RidLocationStopsCanvasComponent)
    .defineCanvasComponent('RidAssignmentsCanvasComponent', RidAssignmentsCanvasComponent)
    .defineCanvasComponent('RidLocationLabelCanvasComponent', RidLocationLabelCanvasComponent)
    .defineCanvasComponent('RidLocationTitleCanvasComponent', RidLocationTitleCanvasComponent)
    .defineCanvasComponent('RidProvinceLabelCanvasComponent', RidProvinceLabelCanvasComponent)
    .defineCanvasComponent('RidProvinceCanvasComponent', RidProvinceCanvasComponent)
    .defineActivity('RidWisselklankenActivity', RidWisselklankenActivity)
    .defineActivity('RidLeeslijstActivity', RidLeeslijstActivity)
    .defineActivity('RidScrapbookActivity', RidScrapbookActivity)
    .defineActivity('RidShowRewardActivity', RidShowRewardActivity)
    .defineActivity('RidScrapbookViewerActivity', RidScrapbookViewerActivity)
    .defineActivity('RidAlgoritmeActivity', RidAlgoritmeActivity)
    .defineCondition('RidIsCurrentStopCompletedCondition', () => {
      const stop = this.$ridHomeworkService.currentStop();
      return !!stop && this.$ridHomeworkService.isStopFinished(stop);
    })
    .defineCondition('RidIsCurrentStopRewardGrantedCondition', () => {
      const stop = this.$ridHomeworkService.currentStop();
      return !!stop && this.$ridRewardService.isStopRewardGranted(stop);
    })
    .defineDebugTab({ id: 'rid-calendar', name: () => 'Calendar', icon: () => '📅', component: RidCalendarDebugTab, order: 40 })
    .defineDebugTab({ id: 'rid-homework', name: () => 'Homework', icon: () => '📚', component: RidHomeworkDebugTab, order: 50 })
    .defineAction('RidPopulateStopsAction', (action) => {
      action.target.stops = action.source.stops;
    })
    .defineAction('RidPopulateAssignmentsAction', (action) => {
      const state = this.$lpApiService.state();
      const locationStopUid = this.$ridHomeworkService.currentStop()?.uid;
      const homeworkStopId = state.homeworkLinks.stops.find((link) => link.locationStopUid === locationStopUid)?.homeworkStopId;
      action.target.assignments = state.homework.stops.find((s) => s.id === homeworkStopId)?.assignments.map((a) => a.config) ?? [];
    })
    .defineAction('RidRollRandomRewardAction', () => {
      this.$ridRewardService.rollRandomReward();
    })
    .build();
}
